/**
 * Sincronizar anúncios a partir do cadastro: fotos, descrição e título do produto vão para os anúncios ligados
 * em todos os canais (todas as contas do Mercado Livre e lojas da Shopee).
 */
import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, X } from 'lucide-react';
import { api, dateTime, int } from '../api.ts';
import { ErrorBox, Tag } from './ui.tsx';

type Field = 'pictures' | 'description' | 'title';
const FIELD_LABEL: Record<Field, string> = { pictures: 'Fotos', description: 'Descrição', title: 'Título' };

export type ContentSelection =
  | { productIds: string[]; label: string }
  | { filter: Record<string, string>; label: string };

interface Preview {
  products: number;
  productsWithListings: number;
  tooMany: boolean;
  unknownSkus: string[];
  unknownSkuCount: number;
  listings: number;
  accounts: { accountId: string; channel: string; channelLabel: string; account: string; listings: number }[];
  skipped: { reason: string; label: string; count: number }[];
}

interface SyncJob {
  id: string;
  status: string;
  fields: Field[];
  force: boolean;
  products: number;
  stats: { listings?: number; done?: number; updated?: number; unchanged?: number; failed?: number; errors?: { sku: string; channel: string; account: string; externalId: string; error: string }[] };
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

const STATUS: Record<string, [string, string]> = {
  queued: ['Na fila', 'info'], running: ['Enviando', 'info'], succeeded: ['Concluída', 'ok'], partial: ['Concluída com erros', 'warn'], failed: ['Falhou', 'err'],
};

const parseSkus = (s: string) => [...new Set(s.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean))];

export function ContentSyncDialog({ selection, onClose }: { selection: ContentSelection | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'selection' | 'skus'>(selection ? 'selection' : 'skus');
  const [skuText, setSkuText] = useState('');
  const [fields, setFields] = useState<Field[]>(['pictures', 'description']);
  const [force, setForce] = useState(false);
  const [withKits, setWithKits] = useState(true);
  const [sentId, setSentId] = useState<string | null>(null);

  const skus = parseSkus(skuText);
  const target = mode === 'skus'
    ? (skus.length ? { skus } : null)
    : selection && ('productIds' in selection ? { productIds: selection.productIds } : { filter: selection.filter });
  const body = target ? { ...target, withKits } : null;

  // A prévia espera a digitação parar.
  const [debounced, setDebounced] = useState(body);
  const key = JSON.stringify(body);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(body), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const preview = useQuery({
    queryKey: ['content-sync-preview', JSON.stringify(debounced)],
    queryFn: () => api<Preview>('/catalog/content-sync/preview', { method: 'POST', json: debounced }),
    enabled: !!debounced,
  });
  const jobs = useQuery({
    queryKey: ['content-sync-jobs'],
    queryFn: () => api<SyncJob[]>('/catalog/content-sync/jobs'),
    refetchInterval: (q) => (q.state.data?.some((j) => j.status === 'queued' || j.status === 'running') ? 2500 : false),
  });
  const send = useMutation({
    mutationFn: () => api<{ syncJobId: string; products: number }>('/catalog/content-sync', { method: 'POST', json: { ...body, fields, force } }),
    onSuccess: (r) => { setSentId(r.syncJobId); qc.invalidateQueries({ queryKey: ['content-sync-jobs'] }); },
  });

  const toggle = (f: Field) => setFields((cur) => (cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]));
  const p = preview.data;
  const ready = !!body && !!p && p.listings > 0 && fields.length > 0 && !send.isPending && !sentId;
  const current = jobs.data?.find((j) => j.id === sentId);

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay" />
        <Dialog.Content className="panel" aria-describedby={undefined}
          style={{ position: 'fixed', top: '6vh', left: '50%', transform: 'translateX(-50%)', width: 'min(640px, calc(100vw - 32px))', maxHeight: '88vh', overflow: 'auto', zIndex: 60 }}>
          <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Dialog.Title asChild><h2 style={{ margin: 0 }}>Sincronizar anúncios</h2></Dialog.Title>
              <span style={{ flex: 1 }} />
              <Dialog.Close className="icon-btn" aria-label="Fechar"><X size={18} /></Dialog.Close>
            </div>
            <p className="small muted" style={{ margin: 0 }}>
              Envia o que está no cadastro da Berytus para os anúncios ligados a estes produtos em <b>todos os canais</b> (todas as contas do Mercado Livre e lojas da Shopee).
              Só vai o que mudou desde o último envio.
            </p>

            <div className="seg" role="tablist" aria-label="Quais produtos">
              {selection && <button type="button" role="tab" aria-pressed={mode === 'selection'} onClick={() => setMode('selection')}>{selection.label}</button>}
              <button type="button" role="tab" aria-pressed={mode === 'skus'} onClick={() => setMode('skus')}>Lista de SKUs</button>
            </div>
            {mode === 'skus' && (
              <div className="field">
                <label htmlFor="cs-skus">SKUs (um por linha, ou separados por vírgula)</label>
                <textarea id="cs-skus" className="input" rows={5} value={skuText} onChange={(e) => setSkuText(e.target.value)} placeholder={'12345\n67890'} style={{ fontFamily: 'var(--mono, monospace)', height: 'auto', minHeight: 110, resize: 'vertical' }} />
                {skus.length > 0 && <span className="small muted">{int(skus.length)} SKU(s)</span>}
              </div>
            )}

            <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 6 }}>
              <legend className="small" style={{ fontWeight: 600, marginBottom: 6 }}>O que enviar</legend>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {(Object.keys(FIELD_LABEL) as Field[]).map((f) => (
                  <label key={f} className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input type="checkbox" className="check" checked={fields.includes(f)} onChange={() => toggle(f)} /> {FIELD_LABEL[f]}
                  </label>
                ))}
              </div>
              {fields.includes('title') && (
                <span className="small muted">Título: no Mercado Livre vai com até 60 letras e o ML não deixa mudar o título de anúncio que já vendeu (o resto segue normalmente).</span>
              )}
              <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" className="check" checked={withKits} onChange={(e) => setWithKits(e.target.checked)} /> Incluir os kits destes produtos
              </label>
              <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" className="check" checked={force} onChange={(e) => setForce(e.target.checked)} /> Reenviar mesmo o que não mudou
              </label>
            </fieldset>

            <div className="panel" style={{ padding: 12 }}>
              {!body ? <span className="small muted">Cole os SKUs para ver os anúncios.</span>
                : preview.isPending || JSON.stringify(debounced) !== key ? <span className="small muted">Conferindo os anúncios…</span>
                : preview.error ? <ErrorBox error={preview.error} />
                : p && (
                  <div style={{ display: 'grid', gap: 6 }}>
                    <div><b>{int(p.listings)}</b> anúncio(s) para atualizar, de {int(p.productsWithListings)} de {int(p.products)} produto(s){p.tooMany && ' (limite de 20.000 produtos por vez)'}.</div>
                    {p.accounts.map((a) => <div key={a.accountId} className="small">{a.channelLabel} · {a.account}: {int(a.listings)}</div>)}
                    {p.skipped.map((s) => <div key={s.reason} className="small muted">Fora: {int(s.count)} — {s.label}.</div>)}
                    {p.unknownSkuCount > 0 && <div className="small" style={{ color: 'var(--warn)' }}>{int(p.unknownSkuCount)} SKU(s) não cadastrados: {p.unknownSkus.slice(0, 15).join(', ')}{p.unknownSkuCount > 15 ? '…' : ''}</div>}
                    {p.products > 0 && p.listings === 0 && <div className="small muted">Nenhum destes produtos tem anúncio ligado em que dê para enviar conteúdo.</div>}
                  </div>
                )}
            </div>

            {send.error && <ErrorBox error={send.error} />}
            {current && <JobLine job={current} />}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn primary" type="button" disabled={!ready} onClick={() => send.mutate()}>
                <RefreshCw size={16} /> {send.isPending ? 'Enviando…' : sentId ? 'Na fila' : `Atualizar ${p?.listings ? int(p.listings) : ''} anúncio(s)`}
              </button>
              <button className="btn" type="button" onClick={onClose}>{sentId ? 'Fechar' : 'Cancelar'}</button>
            </div>

            {!!jobs.data?.filter((j) => j.id !== sentId).length && (
              <details>
                <summary className="small" style={{ cursor: 'pointer' }}>Últimas sincronizações</summary>
                <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
                  {jobs.data.filter((j) => j.id !== sentId).map((j) => <JobLine key={j.id} job={j} />)}
                </div>
              </details>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function JobLine({ job }: { job: SyncJob }) {
  const [label, tone] = STATUS[job.status] ?? [job.status, 'off'];
  const s = job.stats ?? {};
  return (
    <div className="small" style={{ display: 'grid', gap: 4 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Tag tone={tone}>{label}</Tag>
        <span className="muted">{dateTime(job.createdAt)} · {job.fields.map((f) => FIELD_LABEL[f] ?? f).join(', ')} · {int(job.products)} produto(s)</span>
      </div>
      {s.listings != null && (
        <div>
          {int(s.done ?? 0)} de {int(s.listings)} anúncios · {int(s.updated ?? 0)} atualizados · {int(s.unchanged ?? 0)} sem mudança{s.failed ? <> · <b>{int(s.failed)} com erro</b></> : null}
        </div>
      )}
      {job.error && <div style={{ color: 'var(--err)' }}>{job.error}</div>}
      {!!s.errors?.length && (
        <details>
          <summary style={{ cursor: 'pointer' }}>Ver erros</summary>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, display: 'grid', gap: 4 }}>
            {s.errors.slice(0, 50).map((e, i) => (
              <li key={i}><b>{e.sku}</b> · {e.channel === 'shopee' ? 'Shopee' : 'Mercado Livre'} {e.account} · {e.externalId}: {e.error}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
